import { Card, CardContent } from './ui/card';
import { Activity } from 'lucide-react';
import EnhancedClusterOverview from './EnhancedClusterOverview';

// Efficiency tab (Phase 1 — FinOps IA). Lifts the ClickHouse-backed efficiency
// + top-over-provisioned content out of the old inline FinOps block, and pairs
// it with the existing multi-resource EnhancedClusterOverview. Degrades to a
// hint when ClickHouse has no data for the cluster (same pattern as the old tab).

const card = (label, value, sub, accent) => (
  <Card className="border border-slate-200 dark:border-surface-border bg-white dark:bg-surface-dark shadow-sm">
    <CardContent className="p-5">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-text-secondary mb-2">{label}</p>
      <p className={`text-3xl font-semibold tabular-nums ${accent || 'text-slate-900 dark:text-white'}`}>{value}</p>
      {sub && <p className="text-xs text-slate-500 dark:text-text-secondary mt-1">{sub}</p>}
    </CardContent>
  </Card>
);

export default function EfficiencyPanel({ finops, clusterId }) {
  const s = finops?.summary;
  const hasData = s && Object.keys(s).length > 0;
  const wasters = s?.top_wasters || [];

  return (
    <div className="space-y-6">
      {hasData ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {card('CPU Efficiency', `${s.cluster_cpu_efficiency_pct ?? '—'}%`, `${s.cluster_cpu_p95_used ?? 0} / ${s.cluster_cpu_requested ?? 0} cores (used / requested)`)}
            {card('Memory Efficiency', `${s.cluster_mem_efficiency_pct ?? '—'}%`, `${s.cluster_mem_p95_used_gb ?? 0} / ${s.cluster_mem_requested_gb ?? 0} GB (used / requested)`)}
            {card('Est. Monthly Waste', `$${s.est_monthly_waste_usd ?? 0}`, `${s.workload_count ?? 0} workloads · over-provisioned headroom`, 'text-amber-600 dark:text-amber-400')}
          </div>

          <Card className="dark:bg-surface-dark dark:border-surface-border">
            <CardContent className="p-5">
              <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-4">Top Over-Provisioned Workloads</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-500 dark:text-text-secondary border-b border-slate-200 dark:border-surface-border">
                      <th className="py-2 pr-4">Workload</th>
                      <th className="py-2 pr-4 text-right">CPU req → p95</th>
                      <th className="py-2 pr-4 text-right">Mem req → p95 (GB)</th>
                      <th className="py-2 text-right">Est. $/mo waste</th>
                    </tr>
                  </thead>
                  <tbody>
                    {wasters.map((w, i) => (
                      <tr key={i} className="border-b border-slate-100 dark:border-surface-border/50">
                        <td className="py-2 pr-4">
                          <span className="font-medium text-slate-900 dark:text-white">{w.workload}</span>
                          <span className="text-slate-400 dark:text-slate-500 ml-1">{w.namespace}</span>
                        </td>
                        <td className="py-2 pr-4 text-right tabular-nums text-slate-700 dark:text-slate-300">{w.cpu_requested} → {w.cpu_p95_used}</td>
                        <td className="py-2 pr-4 text-right tabular-nums text-slate-700 dark:text-slate-300">{w.mem_requested_gb} → {w.mem_p95_used_gb}</td>
                        <td className="py-2 text-right tabular-nums font-medium text-amber-600 dark:text-amber-400">${w.est_monthly_waste_usd}</td>
                      </tr>
                    ))}
                    {!wasters.length && (
                      <tr><td colSpan={4} className="text-center text-slate-400 py-6">No over-provisioned workloads detected.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-400 dark:text-slate-500 mt-3">Backed by ClickHouse · p95 over the selected window · waste = requested − p95-used</p>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card className="dark:bg-surface-dark dark:border-surface-border">
          <CardContent className="py-12">
            <div className="text-center">
              <Activity className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
              <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">No efficiency data for this cluster yet</h3>
              <p className="text-slate-600 dark:text-text-secondary text-sm max-w-md mx-auto">
                Once this cluster's metrics stream into ClickHouse, used-vs-requested efficiency and top over-provisioned workloads appear here.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Multi-resource cluster overview (existing component, Mongo-backed) */}
      <EnhancedClusterOverview clusterId={clusterId} />
    </div>
  );
}
